import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Bookmark, Sparkles, Calendar, Users, Globe, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/Header';
import { ToolCard } from '@/components/ToolCard';
import { RatingStars } from '@/components/community/RatingStars';
import { CommentsThread } from '@/components/community/CommentsThread';
import { useFavorites } from '@/hooks/useFavorites';
import { useTools } from '@/hooks/useTools';
import { useState } from 'react';
import { cn } from '@/lib/utils';

const platformIcons: Record<string, string> = {
  'Web': '🌐',
  'macOS': '🍎', 
  'Windows': '🪟',
  'iOS': '📱',
  'Android': '🤖',
  'Plugins': '🔌',
};

const getLogoUrl = (tool: any): string => {
  // Priority 1: Check if we have a stored logo in Supabase Storage
  if (tool.logo && !tool.logo.startsWith('http')) {
    return `https://oslzwjyyhugvrzrnwhuc.supabase.co/storage/v1/object/public/tool-logos/${tool.logo}`;
  }
  
  // Priority 2: Use the tool's existing logo URL if it's a full URL
  if (tool.logo && tool.logo.startsWith('http')) {
    return tool.logo;
  }
  
  // Priority 3: Try Clearbit logo API if we have a URL
  if (tool.url) {
    try {
      const domain = new URL(tool.url).hostname;
      return `https://logo.clearbit.com/${domain}`;
    } catch {
      // Fall through to local logos
    }
  }
  
  // Priority 4: Fallback to local logos
  return `/logos/${tool.slug}.svg`;
};

const pricingStyles: Record<string, string> = {
  'Free': 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
  'Freemium': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
  'Paid': 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800',
};

export default function ToolDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { favorites, toggleFavorite } = useFavorites();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: tools = [], isLoading } = useTools();

  const tool = tools.find(t => t.slug === slug);
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-text-secondary">Loading tool details...</p>
        </div>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="min-h-screen bg-background">
        <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-text-primary mb-4">Tool not found</h1>
          <p className="text-text-secondary mb-6">The tool you're looking for doesn't exist.</p>
          <Button asChild>
            <Link to="/">Browse all tools</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isFavorite = tool ? favorites.includes(tool.id) : false;
  const alternativeTools = tool ? tools.filter(t => 
    (tool.alternatives || []).includes(t.slug) && t.slug !== tool.slug
  ).slice(0, 3) : [];

  const relatedTools = tool ? tools.filter(t =>
    t.slug !== tool.slug && 
    !(tool.alternatives || []).includes(t.slug) &&
    ((t.category || []).some(cat => (tool.category || []).includes(cat)) ||
     (t.use_cases || []).some(useCase => (tool.use_cases || []).includes(useCase)))
  ).slice(0, 3) : [];

  return (
    <div className="min-h-screen bg-background">
      <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      
      <main className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Back Navigation */}
        <Button
          variant="ghost"
          onClick={() => navigate('/tools')}
          className="mb-8 -ml-2 text-text-secondary hover:text-primary hover:bg-surface transition-all duration-200"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to tools
        </Button>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-card to-surface rounded-2xl border border-border p-4 md:p-8 mb-8 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 md:gap-8">
            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6 flex-1 min-w-0">
              {/* Tool Logo */}
              <div className="relative flex-shrink-0">
                <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-white p-3 shadow-md border border-border overflow-hidden">
                  <img 
                    src={getLogoUrl(tool)} 
                    alt={`${tool.name} logo`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      // Try fallback sources in order
                      const target = e.target as HTMLImageElement;
                      const currentSrc = target.src;
                      
                      // Try Clearbit as first fallback
                      if (tool.url && !currentSrc.includes('logo.clearbit.com')) {
                        const domain = new URL(tool.url).hostname;
                        target.src = `https://logo.clearbit.com/${domain}`;
                        return;
                      }
                      
                      // Try local logos as second fallback
                      if (!currentSrc.includes('/logos/')) {
                        target.src = `/logos/${tool.slug}.svg`;
                        return;
                      }
                      
                      // Final fallback to letter
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<div class="h-full w-full flex items-center justify-center text-3xl font-bold text-primary bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg">${tool.name.charAt(0)}</div>`;
                      }
                    }}
                  />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                {/* Title and badges */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary font-heading">
                    {tool.name}
                  </h1>
                  {tool.ai_enabled && (
                    <Badge className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border-purple-200 dark:from-purple-900/30 dark:to-pink-900/30 dark:text-purple-300 dark:border-purple-700 text-xs sm:text-sm">
                      <Sparkles className="h-3 w-3 mr-1" />
                      AI-powered
                    </Badge>
                  )}
                  <Badge className={cn("font-medium border text-xs sm:text-sm", pricingStyles[tool.pricing] || "bg-surface text-text-secondary")}>
                    {tool.pricing}
                  </Badge>
                </div>
                
                <p className="text-lg sm:text-xl text-text-secondary mb-4 sm:mb-6 leading-relaxed">
                  {tool.summary}
                </p>

                {/* Categories and Platforms */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {(tool.category || []).map(cat => (
                      <Badge key={cat} variant="outline" className="bg-background hover:bg-surface transition-colors text-xs sm:text-sm">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
                    <div className="flex items-center text-text-tertiary">
                      <Calendar className="h-4 w-4 mr-1" />
                      Updated {new Date(tool.updated_at).toLocaleDateString()}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <span className="text-text-tertiary">Available on:</span>
                      <div className="flex flex-wrap items-center gap-1">
                        {(tool.platforms || []).map(platform => (
                          <span key={platform} className="flex items-center gap-1 px-2 py-1 bg-surface rounded-md text-xs text-text-secondary">
                            <span>{platformIcons[platform] || '💻'}</span>
                            {platform}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[200px]">
              <Button 
                asChild
                size="lg"
                className="bg-gradient-to-r from-primary to-primary-glow hover:from-primary-glow hover:to-primary text-primary-foreground font-semibold shadow-md hover:shadow-lg transition-all duration-200"
              >
                <a 
                  href={tool.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Visit Website
                </a>
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={() => toggleFavorite(tool.id)}
                className={cn(
                  "transition-all duration-200 hover:scale-105",
                  isFavorite 
                    ? "text-red-600 border-red-200 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:border-red-800 dark:bg-red-900/20" 
                    : "hover:border-primary hover:text-primary"
                )}
              >
                <Bookmark className={cn("h-4 w-4 mr-2", isFavorite && "fill-current")} />
                <span className="hidden sm:inline">{isFavorite ? "Remove from Bookmarks" : "Add to Bookmarks"}</span>
                <span className="sm:hidden">{isFavorite ? "Remove" : "Bookmark"}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Content Sections - Stacked Layout */}
        <div className="space-y-8">
          {/* Overview Section */}
          <section className="bg-card rounded-xl border border-border p-4 md:p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-text-primary mb-6 font-heading">Overview</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary leading-relaxed text-lg">
                {tool.summary}
              </p>
            </div>
          </section>

          {/* Quick Facts */}
          <section className="bg-card rounded-xl border border-border p-4 md:p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-text-primary mb-6 font-heading">Quick Facts</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <span className="text-sm font-medium text-text-tertiary uppercase tracking-wide">Pricing</span>
                <p className="text-xl font-semibold text-text-primary mt-2">{tool.pricing}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-text-tertiary uppercase tracking-wide">Use Cases</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(tool.use_cases || []).map(useCase => (
                    <Badge key={useCase} className="bg-accent-light text-accent border-accent/20 text-xs">
                      {useCase}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <span className="text-sm font-medium text-text-tertiary uppercase tracking-wide">Tags</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(tool.tags || []).map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs hover:bg-surface transition-colors">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Alternatives */}
          {alternativeTools.length > 0 && (
            <section className="bg-card rounded-xl border border-border p-4 md:p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-text-primary mb-6 font-heading">Alternatives</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {alternativeTools.map(altTool => (
                  <Link
                    key={altTool.id}
                    to={`/tool/${altTool.slug}`}
                    className="block p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all duration-200 group hover:shadow-sm"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-surface to-background flex items-center justify-center text-sm font-bold text-text-primary border border-border overflow-hidden">
                        <img 
                          src={getLogoUrl(altTool)} 
                          alt={`${altTool.name} logo`}
                          className="w-6 h-6 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<span class="text-sm font-bold text-text-primary">${altTool.name.charAt(0)}</span>`;
                            }
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-text-primary group-hover:text-primary transition-colors duration-200 truncate">
                          {altTool.name}
                        </p>
                        <p className="text-sm text-text-tertiary truncate mt-1">
                          {altTool.summary}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Community Rating */}
          <section className="bg-card rounded-xl border border-border p-4 md:p-8 shadow-sm">
            <RatingStars entityType="tool" entityId={tool.id} />
          </section>

          {/* Related Tools */}
          {relatedTools.length > 0 && (
            <section className="bg-card rounded-xl border border-border p-4 md:p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-text-primary mb-6 font-heading">Related Tools</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedTools.map(relatedTool => (
                  <ToolCard key={relatedTool.id} tool={relatedTool} />
                ))}
              </div>
            </section>
          )}

          {/* Comments */}
          <section className="bg-card rounded-xl border border-border p-4 md:p-8 shadow-sm">
            <CommentsThread entityType="tool" entityId={tool.id} />
          </section>
        </div>
      </main>
    </div>
  );
}